import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

const FilterIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const Pill = ({ label, isActive, category, onToggle }) => (
  <button
    type="button" 
    onClick={(e) => {
      e.preventDefault(); 
      onToggle(category, label);
    }}
    className={`px-3 py-1 rounded-full border text-[13px] font-medium transition-colors ${
      isActive
        ? "bg-[#8B55DD] border-[#8B55DD] text-white" 
        : "bg-white border-[#E5E7EB] text-[#00000080] hover:border-[#D1D5DB]"
    }`}
  >
    {label}
  </button>
);

const ResponseFilterDropdown = ({ 
  initialFilters = { feedback: "All", contexts: [] }, 
  uniqueContexts = [],
  onApply, 
  onClear
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters); 
  
  // Refs
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Dynamic portal position
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const feedbackOptions = ["All", "Good", "Bad"];
  
  // A filter is active if feedback is not "All" or if there are contexts selected
  const hasSelections = appliedFilters.feedback !== "All" || appliedFilters.contexts?.length > 0;
  const isDirty = JSON.stringify(selected) !== JSON.stringify(appliedFilters);

  useEffect(() => {
    setSelected(initialFilters);
    setAppliedFilters(initialFilters);
  }, [JSON.stringify(initialFilters)]);

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideButton = buttonRef.current && !buttonRef.current.contains(event.target);
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      
      if (clickedOutsideButton && clickedOutsideDropdown) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, []);

  // Update position
  const updatePosition = useCallback(() => {
    if (buttonRef.current && isOpen) {
      const rect = buttonRef.current.getBoundingClientRect();
      const rightDistance = window.innerWidth - rect.right;
      
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        right: rightDistance,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Handle singular select for feedback
  const handleFeedbackToggle = (_, value) => {
    setSelected(prev => ({ ...prev, feedback: value }));
  };

  // Handle multi-select for contexts
  const handleContextToggle = (category, value) => {
    setSelected((prev) => {
      const list = prev[category] || []; 
      return {
        ...prev,
        [category]: list.includes(value) ? list.filter((item) => item !== value) : [...list, value],
      };
    });
  };

  const handleApplyClick = () => {
    setAppliedFilters(selected); 
    if (onApply) onApply(selected);
    setIsOpen(false);
  };

  const handleClearClick = () => {
    const clearedState = { feedback: "All", contexts: [] };
    setSelected(clearedState);
    
    if (hasSelections) {
      setAppliedFilters(clearedState);
      if (onClear) {
        onClear();
      } else if (onApply) {
        onApply(clearedState);
      }
    }
    
    setIsOpen(false); 
  };

  const hasSelectedToClear = selected.feedback !== "All" || selected.contexts?.length > 0;

  const dropdownContent = (
    <div 
      ref={dropdownRef}
      className="absolute w-[340px] bg-white rounded-xl shadow-[0px_4px_20px_rgba(0,0,0,0.08)] border border-[#E5E7EB] p-5 font-outfit"
      style={{
        top: `${dropdownPosition.top}px`,
        right: `${dropdownPosition.right}px`,
        zIndex: 2147483647
      }}
    >
      <div className="flex justify-between items-center mb-5">
        <h3 className="text-[16px] font-semibold text-[#1A1A1A]">Filters</h3>
        <button type="button" onClick={() => setIsOpen(false)} className="text-[#A1A1AA] hover:text-[#52525B] transition-colors">
          <CloseIcon />
        </button>
      </div>

      <div className="flex flex-col gap-6">
        <div>
          <h4 className="text-[13px] text-[#00000080] mb-2 font-medium">Feedback Type</h4>
          <div className="flex flex-wrap gap-2">
            {feedbackOptions.map((type) => (
              <Pill 
                key={type} 
                label={type} 
                isActive={selected.feedback === type} 
                category="feedback" 
                onToggle={handleFeedbackToggle} 
              />
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-[13px] text-[#00000080] mb-2 font-medium">Response Context</h4>
          <div className="flex flex-wrap gap-2">
            {uniqueContexts.length === 0 ? (
              <span className="text-[13px] text-[#00000080]">No contexts available</span>
            ) : (
              uniqueContexts.map((ctx) => (
                <Pill 
                  key={ctx} 
                  label={ctx} 
                  isActive={selected.contexts?.includes(ctx)} 
                  category="contexts" 
                  onToggle={handleContextToggle} 
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-3 mt-8">
        <button
          type="button"
          onClick={handleClearClick}
          disabled={!hasSelectedToClear}
          className={`px-4 py-1.5 border rounded text-[14px] font-medium transition-colors ${
            hasSelectedToClear
              ? "border-[#E5E7EB] text-[#52525B] hover:bg-gray-50"
              : "border-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Clear Filters
        </button>
        
        <button
          type="button"
          onClick={handleApplyClick}
          disabled={!isDirty}
          className={`px-6 py-1.5 rounded text-[14px] font-medium transition-colors ${
            isDirty
              ? "bg-[#8B55DD] text-white hover:bg-[#7a48c4]"
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
        >
          Apply
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative inline-block font-outfit" ref={buttonRef}>
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => {
            if (!isOpen) updatePosition();
            setIsOpen(!isOpen);
          }}
          className={`flex items-center gap-2 px-4 py-1.5 border rounded text-[14px] font-medium transition-colors ${
            isOpen || hasSelections
              ? "border-[#8B55DD] text-[#8B55DD] bg-[#F8F3FF]"
              : "border-[#00000036] text-[#52525B] hover:bg-gray-50"
          }`}
        >
          <FilterIcon /> Filter
        </button>
        {hasSelections && (
          <span className="absolute top-0 right-0 block h-2.5 w-2.5 rounded-full bg-[#EF4444] ring-2 ring-white transform translate-x-1/2 -translate-y-1/2"></span>
        )}
      </div>

      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default ResponseFilterDropdown;